import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_PERMISSIONS } from "@queries/index";
import { PermissionTypes } from "@archetypes/gql";
import { Loader } from "@components/loader";
import { Navigate } from "react-router-dom";
const PermissionsContext = createContext<Record<PermissionTypes, boolean> | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { data, loading } = useQuery<{
    getPermission: {
      permissions: Record<PermissionTypes, boolean>;
    };
  }>(GET_PERMISSIONS);

  if (loading) {
    return <Loader />;
  }

  return (
    <PermissionsContext.Provider value={data?.getPermission.permissions}>
      {children}
    </PermissionsContext.Provider>
  );
};

const usePermissions = () => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }

  return context;
};

interface PermissionProps {
  access: PermissionTypes[];
  checkAny?: boolean;
  fallback?: ReactNode;
  redirectUrl?: string;
  children: ((permissions: Record<PermissionTypes, boolean>) => ReactNode) | ReactNode;
}

const Permission: React.FC<PermissionProps> = ({
  access,
  redirectUrl,
  fallback,
  children,
  checkAny,
}) => {
  const permissions = usePermissions();

  if (
    checkAny // Check if all the permission is true or any of the permission is true
      ? access.some((perm) => permissions[perm])
      : access.every((perm) => permissions[perm])
  ) {
    return typeof children === "function" ? children(permissions) : children;
  }

  if (redirectUrl) {
    return <Navigate to={redirectUrl} />;
  }

  if (fallback) {
    return fallback;
  }

  return null;
};

export { PermissionsProvider, Permission };
